import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAccount, useSignMessage } from "wagmi";
import { toast } from "sonner";
import { api, errMsg, setSessionToken } from "../lib/api";

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

export function AppProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [user, setUser] = useState(null);
  const [signing, setSigning] = useState(false);
  const [social, setSocial] = useState({ friends: new Set(), blocked: new Set() });
  const autoTried = useRef(null);
  const [authChecked, setAuthChecked] = useState(false);
  const { address, isConnected } = useAccount();
  const { chainId, connector } = useAccount();
  const { signMessageAsync } = useSignMessage();

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch((e) => setConfigError(errMsg(e)));
    api.get("/auth/me").then((r) => setUser(r.data || null)).catch(() => setUser(null)).finally(() => setAuthChecked(true));
  }, []);

  useEffect(() => {
    const onExpire = () => { setUser(null); toast.message("Your HIVEVORA session expired — sign in again to chat or edit your profile."); };
    window.addEventListener("hive:session-expired", onExpire);
    return () => window.removeEventListener("hive:session-expired", onExpire);
  }, []);

  useEffect(() => {
    if (user && isConnected && address && address.toLowerCase() !== user.address) {
      setSessionToken(null);
      api.post("/auth/logout").finally(() => setUser(null));
    }
  }, [address, isConnected, user]);

  const signIn = useCallback(async () => {
    if (!address) return toast.error("Connect a wallet first");
    setSigning(true);
    try {
      const cid = [4663, 46630].includes(chainId) ? chainId : 4663;
      const { data } = await api.post("/auth/nonce", { address, chain_id: cid });
      const signature = await signMessageAsync({ message: data.message });
      const v = await api.post("/auth/verify", { message: data.message, signature });
      setSessionToken(v.data.token);
      const me = await api.get("/auth/me");
      setUser(me.data || null);
      toast.success(me.data?.alias ? `Welcome back, ${me.data.alias}` : "Signed in — create your profile to get started", { id: "hive-signin" });
    } catch (e) {
      const m = e?.shortMessage || errMsg(e);
      toast.error(/reject|denied/i.test(m) ? "Signature rejected in wallet — tap Sign in to try again" : m);
    } finally {
      setSigning(false);
    }
  }, [address, chainId, signMessageAsync]);

  const signOut = useCallback(async () => {
    setSessionToken(null);
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  }, []);

  const refreshSocial = useCallback(() => {
    if (!user) return setSocial({ friends: new Set(), blocked: new Set() });
    api.get("/social").then((r) => setSocial({ friends: new Set(r.data.friends.map((x) => x.address)), blocked: new Set(r.data.blocked.map((x) => x.address)) })).catch(() => {});
  }, [user]);
  useEffect(() => { refreshSocial(); }, [refreshSocial]);

  useEffect(() => {
    // Auto-prompt only for in-browser wallets; mobile/QR wallets sign in when the user taps "Sign in" (no forced app switch).
    if (!authChecked || !isConnected || !address || user || signing || autoTried.current === address || connector?.type !== "injected") return;
    const t = setTimeout(() => { autoTried.current = address; signIn(); }, 600);
    return () => clearTimeout(t);
  }, [authChecked, isConnected, address, user, signing, signIn, connector]);

  const refreshUser = useCallback(() => api.get("/auth/me").then((r) => setUser(r.data || null)).catch(() => setUser(null)), []);

  return (
    <AppCtx.Provider value={{ config, configError, user, signIn, signOut, signing, refreshUser, social, refreshSocial, isAdmin: user?.role === "admin" }}>
      {children}
    </AppCtx.Provider>
  );
}
